import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { BRAND } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Menu, User, LogOut, LayoutDashboard, ChevronDown, UserCog } from "lucide-react";
import { PlatformMegaMenu, PlatformMegaMenuMobile } from "@/components/PlatformMegaMenu";
import ProfileModal from "@/components/ProfileModal";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";

// Row 1 — primary marketplace + SaaS quick-access (Connect is the round highlight CTA, last).
const primaryLinks = [
  { to: "/industrial-materials",      label: "Industrial" },
  { to: "/marketplace",               label: "Rent" },
  { to: "/buy-new",                   label: "New" },
  { to: "/used",                      label: "Used" },
  { to: "/product/fleet-cloud",       label: "Fleet Cloud" },
  { to: "/product/maintenance-cloud", label: "Maint Cloud" },
];

// Row 2 was removed Feb 2026 — all Business Services links live inside the Platform mega-menu
// for a clean, enterprise-grade single-row header. Kept for mobile sheet backward-compat.
const servicesLinks = [
  { to: "/logistics",      label: "Logistics" },
  { to: "/finance",        label: "Finance" },
  { to: "/insurance",      label: "Insurance" },
  { to: "/amc",            label: "AMC" },
  { to: "/fmc",            label: "FMC" },
  { to: "/breakdown",      label: "Breakdown" },
  { to: "/experts",        label: "Expert" },
  { to: "/intel",          label: "Equipment Intelligence" },
];

// Combined list used in the mobile sheet.
const quickLinks = [...primaryLinks, ...servicesLinks, { to: "/become-partner", label: "Become a Partner" }];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const dashboardHref =
    (user?.role === "admin" || user?.role === "support") ? "/admin"
      : user?.role === "vendor" ? "/vendor"
      : "/dashboard";

  const avatarSrc = user?.avatar_url
    ? (user.avatar_url.startsWith("http") ? user.avatar_url : `${api.defaults.baseURL}${user.avatar_url}`)
    : null;

  return (
    <header className="glass-nav sticky top-0 z-50 w-full" data-testid="site-header">
      <div className="mx-auto w-full max-w-[1700px] px-3 sm:px-5 lg:px-8 xl:px-10 h-16 lg:h-20 xl:h-24 flex items-center gap-3 lg:gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="brand-logo-link">
          <img src={BRAND.logoUrl} alt="EquipmentHub India" className="h-16 w-16 sm:h-20 sm:w-20 xl:h-24 xl:w-24 object-contain shrink-0" style={{ mixBlendMode: "darken" }} data-testid="brand-logo-image" />
          <span className="hidden sm:flex flex-col leading-tight min-w-0">
            <span className="font-display font-black text-base lg:text-lg xl:text-2xl text-ehi-primary tracking-tight truncate">
              Equipment<span className="text-ehi-accent">Hub</span><span className="text-ehi-accent">.</span>India
            </span>
            <span className="hidden xl:inline text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold truncate">
              Heavy Equipment Marketplace
            </span>
          </span>
        </Link>

        {/* Row 1 — primary nav */}
        <nav className="hidden lg:flex flex-1 min-w-0 items-center gap-1 ml-1 xl:ml-2" data-testid="nav-row-primary">
          <PlatformMegaMenu />
          <Link
            to="/post-requirement"
            className="px-3 xl:px-5 py-2 text-sm xl:text-base font-black rounded-full whitespace-nowrap bg-ehi-accent text-white hover:bg-orange-600 shadow-sm ring-1 ring-orange-600/40 transition-colors shrink-0"
            data-testid="nav-post-requirement"
          >
            Post Requirement
          </Link>
          {primaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-2 xl:px-2.5 py-2 text-xs lg:text-sm xl:text-[15px] font-semibold rounded-md transition-colors whitespace-nowrap shrink-0 ${
                  isActive
                    ? "text-ehi-primary bg-slate-100"
                    : "text-slate-700 hover:text-ehi-primary hover:bg-slate-50"
                }`
              }
              data-testid={`nav-${l.to.replace(/[/?=]/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
          {/* Connect — round highlight pill (styled like Get Started) */}
          <NavLink
            to="/connect"
            className={({ isActive }) =>
              `ml-2 mr-2 xl:mr-3 px-4 xl:px-5 py-2 text-sm xl:text-[15px] font-black rounded-full whitespace-nowrap shadow-sm transition-colors shrink-0 ring-1 ${
                isActive
                  ? "bg-indigo-600 text-white ring-indigo-700"
                  : "bg-indigo-600 text-white hover:bg-indigo-700 ring-indigo-700/50"
              }`
            }
            data-testid="nav--connect"
          >
            Connect
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:gap-3 shrink-0 pl-3 lg:pl-5 xl:pl-6 lg:border-l border-slate-200/70 min-w-0">
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 lg:px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 bg-white transition-colors min-w-0 max-w-[220px]"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-ehi-primary text-white grid place-items-center text-xs font-bold shrink-0">
                    {avatarSrc ? (
                      <img src={avatarSrc} alt={user.name} className="w-full h-full object-cover" data-testid="navbar-avatar-img" />
                    ) : (
                      user.name?.[0]?.toUpperCase() || "U"
                    )}
                  </div>
                  <span className="hidden md:inline text-sm font-medium truncate max-w-[120px] xl:max-w-[160px]">{user.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">
                    {user.role}
                  </div>
                  <div className="text-sm font-semibold truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(dashboardHref)} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/my-watchlist")} data-testid="menu-watchlist">
                  <span className="w-4 h-4 mr-2 inline-flex items-center justify-center">♥</span> My Watchlist
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowProfile(true)} data-testid="menu-edit-profile">
                  <UserCog className="w-4 h-4 mr-2" /> Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard/profile")}>
                  <User className="w-4 h-4 mr-2" /> Profile page
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="hidden sm:flex items-center h-10 px-3 text-sm xl:text-base font-semibold text-slate-700 hover:text-ehi-primary whitespace-nowrap rounded-md" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/register" className="btn-accent !rounded-full text-sm whitespace-nowrap" data-testid="nav-register">
                Get Started
              </Link>
            </>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-md border border-slate-200 hover:bg-slate-50" aria-label="Open menu" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[92vw] sm:w-96 overflow-y-auto">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex items-center gap-2 pb-4 border-b border-slate-200">
                <img src={BRAND.logoUrl} alt="EquipmentHub India" className="h-20 w-20 object-contain" style={{ mixBlendMode: "darken" }} />
                <span className="font-display font-black text-lg text-ehi-primary">Equipment<span className="text-ehi-accent">Hub</span> India</span>
              </div>

              {/* Quick links */}
              <div className="flex flex-col gap-1 mt-4">
                {quickLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="px-3 py-3 min-h-[44px] text-sm font-medium text-slate-700 hover:text-ehi-primary hover:bg-slate-50 rounded-md"
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>

              <div className="mt-3 space-y-2">
                <Link
                  to="/post-requirement"
                  onClick={() => setOpen(false)}
                  className="block text-center min-h-[44px] flex items-center justify-center bg-ehi-accent text-white font-black rounded-full px-5 py-3 shadow-sm hover:bg-orange-600"
                  data-testid="nav-mobile-post-requirement"
                >
                  Post Requirement
                </Link>
                <Link
                  to="/connect"
                  onClick={() => setOpen(false)}
                  className="block text-center min-h-[44px] flex items-center justify-center bg-indigo-600 text-white font-black rounded-full px-5 py-3 shadow-sm hover:bg-indigo-700 ring-1 ring-indigo-700/50"
                  data-testid="nav-mobile-connect"
                >
                  Connect
                </Link>
              </div>

              {/* Master Platform Taxonomy (7 categories) */}
              <div className="mt-5 pt-4 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-widest font-black text-ehi-accent mb-2 px-1" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                  Master Platform · 7 Categories
                </div>
                <PlatformMegaMenuMobile onNavigate={() => setOpen(false)} />
              </div>

              {!user && (
                <div className="mt-5 pt-4 border-t border-slate-200 space-y-2">
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-primary text-center block min-h-[44px] flex items-center justify-center">
                    Log in
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-accent !rounded-full text-center block min-h-[44px] flex items-center justify-center">
                    Get Started
                  </Link>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />
    </header>
  );
}
